import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, money } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Calculator, Zap } from "lucide-react";
import { Link } from "react-router-dom";

export default function ServiceNew() {
    const navigate = useNavigate();
    const [serviceTypes, setServiceTypes] = useState([]);
    const [clients, setClients] = useState([]);
    const [priceLists, setPriceLists] = useState([]);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        client_id: "",
        price_list_id: "",
        patient_name: "",
        patient_age: "",
        patient_sex: "M",
        patient_phone: "",
        origin_address: "",
        origin_lat: "",
        origin_lng: "",
        destination_address: "",
        service_type_code: "TBL",
        urgency_level: "normal",
        reason: "",
        needs_oxygen: false,
        needs_doctor: false,
        needs_invoice: true,
        payment_method: "credito",
        channel: "cabina",
        requester_name: "",
        observations: "",
    });

    useEffect(() => {
        (async () => {
            try {
                const [t, c, pl] = await Promise.all([
                    api.get("/service-types"),
                    api.get("/clients"),
                    api.get("/price-lists"),
                ]);
                if (t?.data) setServiceTypes(t.data);
                if (c?.data) setClients(c.data);
                if (pl?.data) setPriceLists(pl.data);
            } catch (e) { console.warn(e); }
        })();
    }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Auto-aplicar la lista de precios del cliente seleccionado (igual que el Cotizador).
    useEffect(() => {
        if (!form.client_id || !clients.length) return;
        const c = clients.find((x) => x.id === form.client_id);
        if (c?.price_list_id && c.price_list_id !== form.price_list_id) {
            setForm((f) => ({ ...f, price_list_id: c.price_list_id }));
        }
        if (!c?.price_list_id && form.price_list_id) {
            setForm((f) => ({ ...f, price_list_id: "" }));
        }
    }, [form.client_id, clients]); // eslint-disable-line react-hooks/exhaustive-deps

    const activePriceList = priceLists.find((p) => p.id === form.price_list_id);
    const selectedClient = clients.find((c) => c.id === form.client_id);

    // Merge catálogo global + custom_service_types de la lista activa.
    // Si un código existe en ambos, gana el de la lista (sobrescribe precio).
    const mergedServiceTypes = (() => {
        const map = new Map();
        serviceTypes.forEach((s) => map.set(s.code, { ...s, source: "catalog" }));
        (activePriceList?.custom_service_types || []).forEach((s) => {
            map.set(s.code, {
                code: s.code,
                name: s.name,
                base_price: s.base_price || 0,
                source: "price_list",
            });
        });
        return Array.from(map.values());
    })();

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form };
            if (payload.patient_age) payload.patient_age = parseInt(payload.patient_age);
            else delete payload.patient_age;
            if (payload.origin_lat === "" || payload.origin_lat === null) delete payload.origin_lat;
            if (payload.origin_lng === "" || payload.origin_lng === null) delete payload.origin_lng;
            if (!payload.client_id) delete payload.client_id;
            if (!payload.price_list_id) delete payload.price_list_id;
            const { data } = await api.post("/services", payload);
            toast.success(`Servicio creado: ${data.folio}`);
            navigate(`/services/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    // ============ Live pricing preview ============
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const debounceRef = useRef(null);
    const previewParams = useMemo(() => ({
        service_type_code: form.service_type_code,
        client_id: form.client_id || undefined,
        price_list_id: form.price_list_id || undefined,
        is_foreign: !!form.is_foreign,
    }), [form.service_type_code, form.client_id, form.price_list_id, form.is_foreign]);

    useEffect(() => {
        if (!previewParams.service_type_code) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setPreviewLoading(true);
            try {
                const cleanParams = Object.fromEntries(
                    Object.entries(previewParams).filter(([, v]) => v !== undefined && v !== "")
                );
                const r = await api.get("/pricing/preview", { params: cleanParams });
                if (r?.data) setPreview(r.data);
            } catch (e) {
                setPreview(null);
            } finally {
                setPreviewLoading(false);
            }
        }, 350);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [previewParams]);
    return (
        <div className="max-w-5xl" data-testid="service-new-page">
            <Link to="/services" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-3">
                <ArrowLeft className="w-3 h-3" /> Volver a servicios
            </Link>

            <form onSubmit={submit} className="space-y-4">
                <div className="fm-card p-5">
                    <h3 className="font-display font-bold text-base mb-4 pb-3 border-b border-slate-200">Datos del servicio</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="fm-label">Cliente / Convenio</label>
                            <select value={form.client_id} onChange={(e) => set("client_id", e.target.value)} className="fm-input" data-testid="client-select">
                                <option value="">Particular (sin convenio)</option>
                                {clients.map((c) => (
                                    <option key={c.id} value={c.id}>{c.business_name}{c.price_list_id ? " · ★" : ""}</option>
                                ))}
                            </select>
                            {selectedClient?.price_list_id && form.price_list_id === selectedClient.price_list_id && activePriceList && (
                                <div className="text-[11px] text-indigo-700 mt-1" data-testid="service-new-pricelist-banner">
                                    ⚡ Lista del convenio aplicada: <b>{activePriceList.name}</b>
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="fm-label">Lista de precios</label>
                            <select value={form.price_list_id} onChange={(e) => set("price_list_id", e.target.value)} className="fm-input" data-testid="service-new-pricelist">
                                <option value="">Pública (default)</option>
                                {priceLists.map((pl) => (
                                    <option key={pl.id} value={pl.id}>
                                        {pl.name}{pl.is_default ? " (público)" : ""}
                                    </option>
                                ))}
                            </select>
                            {selectedClient?.price_list_id && form.price_list_id !== selectedClient.price_list_id && (
                                <button
                                    type="button"
                                    onClick={() => set("price_list_id", selectedClient.price_list_id)}
                                    className="text-[11px] text-amber-700 mt-1 hover:underline"
                                    data-testid="service-new-restore-list"
                                >
                                    ⚠ Cambiaste la lista del convenio · click para restaurar
                                </button>
                            )}
                        </div>
                        <div>
                            <label className="fm-label">Canal de origen</label>
                            <select value={form.channel} onChange={(e) => set("channel", e.target.value)} className="fm-input">
                                <option value="cabina">Cabina interna</option>
                                <option value="portal">Portal de cliente</option>
                                <option value="whatsapp">WhatsApp</option>
                                <option value="telefono">Llamada telefónica</option>
                                <option value="referido">Referido</option>
                            </select>
                        </div>
                        <div>
                            <label className="fm-label">Solicitante</label>
                            <input value={form.requester_name} onChange={(e) => set("requester_name", e.target.value)} className="fm-input" placeholder="Nombre de quien solicita" />
                        </div>
                    </div>
                </div>

                <div className="fm-card p-5">
                    <h3 className="font-display font-bold text-base mb-4 pb-3 border-b border-slate-200">Paciente</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="md:col-span-2">
                            <label className="fm-label">Nombre del paciente *</label>
                            <input required value={form.patient_name} onChange={(e) => set("patient_name", e.target.value)} className="fm-input" data-testid="patient-name-input" />
                        </div>
                        <div>
                            <label className="fm-label">Edad</label>
                            <input type="number" value={form.patient_age} onChange={(e) => set("patient_age", e.target.value)} className="fm-input" />
                        </div>
                        <div>
                            <label className="fm-label">Sexo</label>
                            <select value={form.patient_sex} onChange={(e) => set("patient_sex", e.target.value)} className="fm-input">
                                <option value="M">Masculino</option>
                                <option value="F">Femenino</option>
                                <option value="O">Otro</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="fm-label">Teléfono</label>
                            <input value={form.patient_phone} onChange={(e) => set("patient_phone", e.target.value)} className="fm-input" />
                        </div>
                    </div>
                </div>

                <div className="fm-card p-5">
                    <h3 className="font-display font-bold text-base mb-4 pb-3 border-b border-slate-200">Servicio</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="fm-label">Tipo de servicio *</label>
                            <select required value={form.service_type_code} onChange={(e) => set("service_type_code", e.target.value)} className="fm-input" data-testid="service-type-select">
                                {activePriceList?.custom_service_types?.length > 0 ? (
                                    <>
                                        <optgroup label={`Catálogo del convenio · ${activePriceList.name}`}>
                                            {activePriceList.custom_service_types.map((s) => (
                                                <option key={`pl-${s.code}`} value={s.code}>
                                                    {s.code} — {s.name} (${s.base_price})
                                                </option>
                                            ))}
                                        </optgroup>
                                        <optgroup label="Catálogo público (FindMed)">
                                            {serviceTypes
                                                .filter((s) => !activePriceList.custom_service_types.some((c) => c.code === s.code))
                                                .map((t) => (
                                                    <option key={t.code} value={t.code}>{t.code} — {t.name} (${t.base_price})</option>
                                                ))}
                                        </optgroup>
                                    </>
                                ) : (
                                    mergedServiceTypes.map((t) => (
                                        <option key={t.code} value={t.code}>{t.code} — {t.name} (${t.base_price})</option>
                                    ))
                                )}
                            </select>
                        </div>
                        <div>
                            <label className="fm-label">Nivel de urgencia</label>
                            <select value={form.urgency_level} onChange={(e) => set("urgency_level", e.target.value)} className="fm-input">
                                <option value="baja">Baja</option>
                                <option value="normal">Normal</option>
                                <option value="alta">Alta</option>
                                <option value="critica">Crítica</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="fm-label">Motivo / sintomatología</label>
                            <input value={form.reason} onChange={(e) => set("reason", e.target.value)} className="fm-input" placeholder="Ej. Dolor torácico, traslado programado..." />
                        </div>
                        <div>
                            <label className="fm-label">Origen *</label>
                            <input
                                required
                                value={form.origin_address}
                                onChange={(e) => set("origin_address", e.target.value)}
                                className="fm-input"
                                placeholder="Dirección o pega coordenadas (ej. 19.4326, -99.1332)"
                                data-testid="origin-address-input"
                                onBlur={(e) => {
                                    const m = (e.target.value || "").match(/^\s*(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)\s*$/);
                                    if (m) {
                                        const lat = parseFloat(m[1]); const lng = parseFloat(m[2]);
                                        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                                            set("origin_lat", lat); set("origin_lng", lng);
                                        }
                                    }
                                }}
                            />
                            <div className="grid grid-cols-2 gap-2 mt-2">
                                <input
                                    type="number"
                                    step="0.000001"
                                    value={form.origin_lat || ""}
                                    onChange={(e) => set("origin_lat", e.target.value === "" ? "" : parseFloat(e.target.value))}
                                    className="fm-input text-xs"
                                    placeholder="Latitud (opcional)"
                                    data-testid="origin-lat-input"
                                />
                                <input
                                    type="number"
                                    step="0.000001"
                                    value={form.origin_lng || ""}
                                    onChange={(e) => set("origin_lng", e.target.value === "" ? "" : parseFloat(e.target.value))}
                                    className="fm-input text-xs"
                                    placeholder="Longitud (opcional)"
                                    data-testid="origin-lng-input"
                                />
                            </div>
                            <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-500">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!navigator.geolocation) return toast.error("Geolocalización no disponible");
                                        navigator.geolocation.getCurrentPosition(
                                            (pos) => {
                                                set("origin_lat", pos.coords.latitude);
                                                set("origin_lng", pos.coords.longitude);
                                                if (!form.origin_address) set("origin_address", `${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`);
                                                toast.success("Coordenadas capturadas");
                                            },
                                            () => toast.error("No pude obtener tu ubicación")
                                        );
                                    }}
                                    className="font-bold uppercase tracking-wider text-sky-700 hover:text-sky-900"
                                    data-testid="origin-geo-btn"
                                >📍 Usar mi ubicación</button>
                                <span className="text-slate-400">·</span>
                                <span>Puedes pegar coordenadas directas en el campo de origen o capturarlas aparte.</span>
                            </div>
                        </div>
                        <div>
                            <label className="fm-label">Destino</label>
                            <input value={form.destination_address} onChange={(e) => set("destination_address", e.target.value)} className="fm-input" placeholder="Hospital, domicilio, etc." />
                        </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            ["needs_oxygen", "Requiere oxígeno"],
                            ["needs_doctor", "Requiere médico"],
                            ["needs_extra_paramedic", "Paramédico extra"],
                            ["needs_nurse", "Enfermería"],
                            ["needs_special_equipment", "Equipo especial"],
                            ["is_foreign", "Traslado foráneo"],
                            ["needs_wait", "Espera"],
                            ["needs_invoice", "Requiere factura"],
                        ].map(([k, lbl]) => (
                            <label key={k} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                                <input type="checkbox" checked={form[k] || false} onChange={(e) => set(k, e.target.checked)} className="rounded-sm" />
                                <span>{lbl}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="fm-card p-5">
                    <h3 className="font-display font-bold text-base mb-4 pb-3 border-b border-slate-200">Pago y observaciones</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="fm-label">Método de pago</label>
                            <select value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)} className="fm-input">
                                <option value="credito">Crédito (convenio)</option>
                                <option value="contado">Contado</option>
                                <option value="transferencia">Transferencia</option>
                                <option value="tarjeta">Tarjeta</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="fm-label">Observaciones</label>
                            <textarea value={form.observations} onChange={(e) => set("observations", e.target.value)} className="fm-input" rows={3} />
                        </div>
                    </div>
                </div>

                {/* ============ Mini preview de precios en vivo ============ */}
                <PricingPreviewPanel
                    preview={preview}
                    loading={previewLoading}
                    activePriceList={activePriceList}
                    selectedClient={selectedClient}
                />

                <div className="flex items-center justify-between gap-3 sticky bottom-0 bg-slate-100 py-3">
                    <div className="text-xs text-slate-600">
                        {preview && (
                            <span>
                                Total estimado:
                                <b className="font-mono tabular-nums text-indigo-700 ml-2 text-base" data-testid="sticky-total">{money(preview.total || 0)}</b>
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/services" className="fm-btn-outline">Cancelar</Link>
                        <button type="submit" disabled={saving} className="fm-btn-accent" data-testid="submit-service-btn">
                            <Save className="w-3.5 h-3.5" strokeWidth={2} />
                            {saving ? "Creando..." : "Crear solicitud"}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}

function PricingPreviewPanel({ preview, loading, activePriceList, selectedClient }) {
    if (!preview) {
        return (
            <div className="fm-card p-5 border-l-4 border-l-indigo-500" data-testid="pricing-preview-panel-empty">
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                    <Calculator className="w-4 h-4" strokeWidth={1.5} />
                    {loading ? "Calculando preview..." : "Selecciona cliente y tipo de servicio para ver el precio en vivo"}
                </div>
            </div>
        );
    }
    const breakdown = preview.breakdown || [];
    const hasDiscount = (preview.discount_amount || 0) > 0;
    const cashNoInvoice = !!preview.cash_no_invoice;
    return (
        <div className="fm-card border-l-4 border-l-indigo-600 overflow-hidden" data-testid="pricing-preview-panel">
            <div className="bg-indigo-50 px-5 py-3 flex items-center justify-between border-b border-indigo-100">
                <div>
                    <div className="font-display font-bold text-sm flex items-center gap-2">
                        <Calculator className="w-4 h-4 text-indigo-600" strokeWidth={2} />
                        Cotización en vivo
                        {loading && <span className="text-[10px] text-slate-500 font-mono uppercase animate-pulse">actualizando...</span>}
                    </div>
                    <div className="text-[11px] text-slate-600 mt-0.5">
                        {selectedClient?.business_name ? `Para: ${selectedClient.business_name}` : "Cliente particular"}
                        {activePriceList && (
                            <span className="ml-2 text-indigo-700 font-bold inline-flex items-center gap-1">
                                <Zap className="w-3 h-3" /> {activePriceList.name}
                            </span>
                        )}
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Total estimado</div>
                    <div className="font-mono text-2xl font-black tabular-nums text-indigo-700" data-testid="preview-total">
                        {money(preview.total || 0)}
                    </div>
                </div>
            </div>
            <div className="px-5 py-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                {breakdown.map(([label, amount], i) => (
                    <div key={i} className="flex justify-between border-b border-slate-100 py-1">
                        <span className="text-slate-600">{label}</span>
                        <span className="font-mono tabular-nums font-semibold">{money(amount)}</span>
                    </div>
                ))}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <KPIBox label="Subtotal" value={money(preview.subtotal_after_discount || preview.subtotal || 0)} testid="preview-subtotal" />
                {hasDiscount && (
                    <KPIBox label="Descuento" value={`-${money(preview.discount_amount)}`} accent="text-emerald-700" />
                )}
                <KPIBox
                    label={cashNoInvoice ? "IVA (exento)" : "IVA 16%"}
                    value={cashNoInvoice ? "EXENTO" : money(preview.iva || 0)}
                    accent={cashNoInvoice ? "text-amber-700" : "text-slate-700"}
                />
                <KPIBox label="Total" value={money(preview.total || 0)} accent="text-indigo-700 text-base" testid="preview-total-kpi" />
            </div>
            {cashNoInvoice && (
                <div className="px-5 py-2 bg-amber-50 border-t border-amber-200 text-[11px] text-amber-900 font-medium">
                    ⚠ Pago en efectivo sin factura · IVA no se cobrará.
                </div>
            )}
            <div className="px-5 py-2 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-500 italic">
                Este preview se calcula automáticamente con la lista del convenio. Los cargos extra (km foráneo, espera, casetas, insumos) se agregan después desde el detalle del servicio.
            </div>
        </div>
    );
}

function KPIBox({ label, value, accent = "text-slate-900", testid }) {
    return (
        <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2" data-testid={testid}>
            <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
            <div className={`font-mono tabular-nums font-bold text-sm ${accent}`}>{value}</div>
        </div>
    );
}
